import { Camera } from './Camera';
import { Entity } from './Entity';
import { Input } from './Input';
import { ColliderFacet } from './facets/ColliderFacet';
import { newV2 } from './lib/V2';

interface Time {
  timeScale: number,
  now: number,
  nowUnscaled: number,
}

export class Engine {

  viewport = newV2();
  input: Input = new Input();
  entities: Entity[] = [];
  deletionQueue: Entity[] = [];

  private postUpdateQueue: Array<() => void> | null = null;

  time: Time = {
    timeScale: 1,
    now: performance.now(),
    nowUnscaled: performance.now(),
  };

  camera: Camera = {
    position: newV2(),
    scale: 1,
    renderOrderComparator: (e1, e2) => e1.zIndex - e2.zIndex
      || e1.transform.position[1] - e2.transform.position[1],
  };

  add<E extends Entity<any>>(entity: E, overrides?: Partial<E>) {
    Object.assign(entity, overrides);
    this.entities.push(entity);
    entity.facets.forEach(f => f.start(this));
    return entity;
  }

  destroy(entity: Entity) {
    this.deletionQueue.push(entity);
  }

  queuePostUpdate(cb: () => void) {
    this.postUpdateQueue = this.postUpdateQueue || [];
    this.postUpdateQueue.push(cb);
  }

  step = (deltaActual: number) => {
    const delta = Math.min(deltaActual, 17);
    this.time.now += delta * this.time.timeScale;
    this.time.nowUnscaled += delta;
    this.entities.forEach(entity => {
      entity.facets.forEach(f => f.update(delta * this.time.timeScale, this));
    });
    this.entities.forEach(entity => {
      entity.getFacets(ColliderFacet).forEach(c => { c.collisions = []; });
    });
    if (this.postUpdateQueue) {
      this.postUpdateQueue.forEach(cb => cb());
      this.postUpdateQueue = null;
    }
    this.entities.forEach(entity => {
      entity.facets.forEach(f => f.physicsUpdate(delta * this.time.timeScale, this));
    });
    this.deletionQueue.forEach(entity => {
      entity.facets.forEach(f => f.stop(this));
      const i = this.entities.indexOf(entity);
      if (i >= 0) this.entities.splice(i, 1);
    });
    this.deletionQueue = [];
    this.input.reset();
  };
}
